<template>
  <div class="content-wrapper">
    <section class="content-header">
      <h1>Nudge Console</h1>
      <ol class="breadcrumb">
        <li><a href="#"><i class="fa fa-dashboard"></i> Home</a></li>
        <li class="active">Analytics</li>
      </ol>
    </section>

    <section class="content nc-page">
      <campaign-analytics
        v-if="campaignId && detail"
        :campaign="detail.campaign"
        :totals="detail.totals"
        :variants="detail.variants"
        :daily="detail.daily"
        :funnel="detail.funnel"
        :buttons="detail.buttons"
        :preview="detail.preview"
        :transcript-cohort-sizes="cohortSizes"
        :from="trendFrom"
        :to="trendTo"
        @back="closeDetail"
        @refresh="fetchDetail"
        @range-change="onRangeChange"
        @export="exportResponses"
        @cohort-sizes="loadCohortSizes"
        @export-transcripts="exportTranscripts"
      />

      <template v-else-if="!campaignId">
      <div class="na-tiles">
        <div class="nc-surface na-tile">
          <div class="na-tile-label">Nudges delivered</div>
          <div class="na-tile-value">{{ totals.delivered.toLocaleString() }}</div>
          <div class="na-tile-sub">{{ campaigns.length }} campaign{{ campaigns.length === 1 ? '' : 's' }} in range</div>
        </div>
        <div class="nc-surface na-tile">
          <div class="na-tile-label">Delivery rate</div>
          <div class="na-tile-value">{{ totals.deliveryRate }}%</div>
          <div class="na-tile-sub">{{ totals.failed.toLocaleString() }} failed of {{ totals.attempts.toLocaleString() }} attempts</div>
        </div>
        <div class="nc-surface na-tile">
          <div class="na-tile-label">Users nudged</div>
          <div class="na-tile-value">{{ totals.uniqueUsers.toLocaleString() }}</div>
          <div class="na-tile-sub">Distinct accounts</div>
        </div>
        <div class="nc-surface na-tile">
          <div class="na-tile-label">Responses</div>
          <div class="na-tile-value">{{ totals.responded.toLocaleString() }}</div>
          <div class="na-tile-sub">Nudges that got a reply</div>
        </div>
        <div class="nc-surface na-tile">
          <div class="na-tile-label">Response rate</div>
          <div class="na-tile-value">{{ totals.responseRate }}%</div>
          <div class="na-tile-sub">Nudges replied to ÷ nudges delivered</div>
        </div>
      </div>

      <div class="nc-surface nc-filters">
        <div class="nc-field nc-field-grow">
          <label for="na-search">Search</label>
          <input
            id="na-search"
            v-model="searchText"
            type="text"
            class="nc-input"
            placeholder="Campaign name — press Enter"
            @keyup.enter="reload"
          />
        </div>
        <div class="nc-field">
          <label for="na-from">From</label>
          <input id="na-from" v-model="fromDt" type="date" class="nc-input" @change="reload" />
        </div>
        <div class="nc-field">
          <label for="na-to">To</label>
          <input id="na-to" v-model="toDt" type="date" class="nc-input" @change="reload" />
        </div>
        <div class="nc-field">
          <label for="na-scope">Campaigns</label>
          <nv-select id="na-scope" v-model="scope" class="nc-input" @change="reload">
            <option value="all">All campaigns</option>
            <option value="nudge-console">Nudge Console only</option>
          </nv-select>
        </div>
      </div>

      <div v-if="truncated" class="nc-notice nc-notice-warn">
        <i class="fa fa-exclamation-triangle"></i>
        <span>
          More than {{ limit }} campaigns sent nudges in this range, so the totals above cover only
          the {{ limit }} with the most delivered. Narrow the date range or search by name for exact
          numbers.
        </span>
      </div>

      <div v-if="!loading && !campaigns.length" class="nc-surface nc-empty">
        <div class="nc-empty-icon"><i class="fa fa-line-chart"></i></div>
        <div class="nc-empty-title">No nudges were sent in this range</div>
        <div class="nc-empty-text">
          Only campaigns that actually sent something appear here. Widen the date range, or clear
          the filters.
        </div>
      </div>

      <div v-else class="nc-surface na-table-wrap">
        <table class="na-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Window</th>
              <th class="na-num">Users</th>
              <th class="na-num">Sent</th>
              <th class="na-num">Delivered</th>
              <th class="na-num">Failed</th>
              <th class="na-num">Responses</th>
              <th class="na-num">Response rate</th>
              <th class="na-col-actions"><span class="na-sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in campaigns">
              <tr :key="row.id">
                <td>
                  <button
                    class="na-row-toggle"
                    :aria-expanded="expanded[row.id] ? 'true' : 'false'"
                    @click="toggleRow(row)"
                  >
                    <i class="fa fa-caret-right na-caret" :class="{ 'is-open': expanded[row.id] }"></i>
                    <span class="na-row-name">{{ row.opportunity_name }}</span>
                  </button>
                  <div class="na-row-meta">
                    {{ row.country_name || '—' }} · {{ row.customer_name || 'No customer' }}
                  </div>
                </td>
                <td>
                  {{ windowText(row) }}
                  <div class="na-row-meta na-row-meta-flush">{{ groupsText(row) }}</div>
                </td>
                <td class="na-num">{{ row.unique_users.toLocaleString() }}</td>
                <td class="na-num">{{ row.attempts.toLocaleString() }}</td>
                <td class="na-num">{{ row.delivered.toLocaleString() }}</td>
                <td class="na-num">
                  <span :class="row.failed ? 'na-failed' : ''">{{ row.failed.toLocaleString() }}</span>
                </td>
                <td class="na-num">{{ row.responded.toLocaleString() }}</td>
                <td class="na-num">
                  <span class="na-rate">
                    {{ responseRateOf(row) }}%
                    <span class="na-rate-bar">
                      <span class="na-rate-bar-fill" :style="{ width: Math.min(responseRateOf(row), 100) + '%' }"></span>
                    </span>
                  </span>
                </td>
                <td class="na-col-actions">
                  <!-- Expanding the row shows the A/B split; this opens the campaign's own
                       page — trend chart, funnel progression and the per-user export. Same
                       ?campaignId= route the Dashboard's Analytics button uses, so there is
                       one way in rather than two. -->
                  <router-link
                    class="nc-btn nc-btn-sm nc-btn-quiet na-detail-link"
                    title="Open the full campaign view"
                    :to="{ path: '/nudge-console/analytics', query: { campaignId: row.id } }"
                  >
                    Details <i class="fa fa-angle-right"></i>
                  </router-link>
                </td>
              </tr>
              <tr
                v-for="group in (expanded[row.id] ? row.group_list : [])"
                :key="`${row.id}-${group.group_id}`"
                class="na-group-row"
              >
                <td class="na-group-name">
                  <span class="na-group-label">{{ group.group_name || 'Unnamed group' }}</span>
                  <span v-if="group.split_percent" class="na-group-split">{{ group.split_percent }}% of traffic</span>
                </td>
                <td>{{ group.msg_template_name || 'Generated content' }}</td>
                <td class="na-num">{{ group.unique_users.toLocaleString() }}</td>
                <td class="na-num">{{ group.attempts.toLocaleString() }}</td>
                <td class="na-num">{{ group.delivered.toLocaleString() }}</td>
                <td class="na-num">{{ group.failed.toLocaleString() }}</td>
                <td class="na-num">{{ group.responded.toLocaleString() }}</td>
                <td class="na-num">{{ responseRateOf(group) }}%</td>
                <td class="na-col-actions"></td>
              </tr>
            </template>
          </tbody>
        </table>

        <p class="na-table-note">
          Sent, delivered, failed and responses count individual nudges, so response rate is
          nudges replied to ÷ nudges delivered. Users counts distinct accounts.
          <template v-if="hasSharedVariantUsers">
            On a campaign that re-fires the A/B split is applied per send, so one account can
            receive both variants — variant Users counts can add up to more than the
            campaign's. The message columns still add up exactly.
          </template>
        </p>
      </div>
      </template>
    </section>

    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br />Please wait...</BlockUI>
  </div>
</template>

<script>
import api from '../../../utils/api'
import CampaignAnalytics from './analytics/CampaignAnalytics.vue'
import { formatDate, downloadCSVCustomColumns } from '../../../utils/utilities'
import { describeSession, percentage, isoDaysAgo, todayIso, isoOf, addDays } from './constants'

// The trend window opens on the campaign's first week, counted from its scheduled start
// or — for always-on, which has no schedule — the day it was created. The server resolves
// which of those applies and returns it as `launched_at`.
const DEFAULT_TREND_DAYS = 7

export default {
  name: 'nudge-analytics',
  components: { CampaignAnalytics },
  data () {
    return {
      loading: false,
      // Detail view state. `?campaignId=` swaps the whole page over to one campaign; the
      // list above is only rendered when there's no id.
      detail: null,
      // Transcript cohort pool sizes, fetched only when the transcript modal opens.
      cohortSizes: null,
      trendFrom: '',
      trendTo: '',
      // Rows come from POST /nudge-campaign-analytics, which reads "user-engagements"
      // directly. It already applies the date, search, scope and campaign filters and
      // rolls per-group numbers into each campaign's group_list, so there's nothing left
      // to filter or merge client-side.
      campaigns: [],
      truncated: false,
      limit: 0,
      expanded: {},
      fromDt: isoDaysAgo(30),
      toDt: todayIso(),
      searchText: '',
      // A select rather than a checkbox: this changes which campaigns the totals above are
      // computed from, so it's a scope choice, not an on/off refinement. The Dashboard
      // shows the same distinction as a per-card column instead, since there you want to
      // see everything and know which is which.
      //
      // Defaults to every campaign. NUDGE_CONSOLE_CAMPAIGN_SQL matches on shape rather than
      // on a real "created here" column, and it doesn't recognise Batch OOS at all — so
      // narrowing by default hides real campaigns and makes the tiles above read low, with
      // nothing on screen to say why.
      scope: 'all',
      campaignId: null
    }
  },
  computed: {
    hasSharedVariantUsers () {
      return this.campaigns.some(row => {
        const groups = row.group_list || []
        if (groups.length < 2) return false
        const groupUsers = groups.reduce((sum, g) => sum + (g.unique_users || 0), 0)
        return groupUsers > row.unique_users
      })
    },
    totals () {
      const sum = key => this.campaigns.reduce((total, row) => total + row[key], 0)
      const attempts = sum('attempts')
      const delivered = sum('delivered')
      const responded = sum('responded')
      return {
        attempts,
        delivered,
        failed: sum('failed'),
        // Distinct users can't be summed without double-counting anyone nudged by two
        // campaigns. Accepted: this is the sum of per-campaign distinct counts, which
        // reads as "user-campaign pairs" once someone is in more than one.
        uniqueUsers: sum('unique_users'),
        responded,
        deliveryRate: percentage(delivered, attempts),
        responseRate: percentage(responded, delivered)
      }
    }
  },
  mounted () {
    this.campaignId = this.$route.query.campaignId || null
    this.reload()
  },
  watch: {
    // Covers arriving from the Dashboard's Analytics button and the browser back button,
    // both of which change the query without remounting this component.
    '$route.query.campaignId' (value) {
      this.campaignId = value || null
      this.reload()
    }
  },
  methods: {
    closeDetail () {
      this.campaignId = null
      this.detail = null
      this.$router.replace({ path: '/nudge-console/analytics' })
      this.reload()
    },

    // Pool sizes for the transcript modal's cohort select. Silent on failure — the modal
    // still works, it just can't show "42 replied" next to each option.
    async loadCohortSizes () {
      try {
        const { data, status } = await api.buildRequest('nudge-campaign-transcript-audience', 'POST', {
          campaignId: this.campaignId,
          cohort: 'any',
          limit: 1
        })
        if (status !== 200) return
        this.cohortSizes = (data && data.payload && data.payload.cohortSizes) || null
      } catch (err) {
        this.cohortSizes = null
      }
    },

    // Two steps, deliberately: resolve the cohort into account ids here, then hand them to
    // /audience/exportTranscripts — the SAME endpoint the Audience Builder's "Get
    // Transcripts" modal posts to. It already creates the Drive folder, writes the criteria
    // doc and forks uploadTranscript.js, so transcript generation isn't reimplemented.
    async exportTranscripts ({ cohort, limit }) {
      this.loading = true
      try {
        const { data, status } = await api.buildRequest('nudge-campaign-transcript-audience', 'POST', {
          campaignId: this.campaignId,
          cohort,
          limit
        })
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        const payload = (data && data.payload) || {}
        this.cohortSizes = payload.cohortSizes || this.cohortSizes

        const accountIds = payload.accountIds || []
        if (!accountIds.length) {
          this.$swal({ type: 'info', text: 'No delivered nudges match that cohort yet, so there are no transcripts to export.' })
          return
        }

        // `params` is written verbatim into a "criteria" doc beside the transcripts in
        // Drive, so whoever opens the folder later can see exactly what was exported.
        const params = {
          source: 'Nudge Console — Campaign Analytics',
          campaignId: this.campaignId,
          campaignName: payload.campaignName,
          cohort,
          sampling: 'random',
          requested: limit,
          returned: accountIds.length,
          trendWindow: this.trendFrom && this.trendTo ? `${this.trendFrom} to ${this.trendTo}` : 'campaign lifetime',
          exportedAt: new Date().toISOString()
        }

        const { data: exportData, status: exportStatus } = await api.buildRequest('audience/exportTranscripts', 'POST', {
          audience: accountIds,
          audienceName: payload.campaignName || `campaign_${this.campaignId}`,
          countryName: payload.countryName || '',
          params,
          isGenAITranscript: false
        })
        if (exportStatus !== 200) {
          throw new Error((exportData && exportData.payload && exportData.payload.message) || 'Transcript export failed')
        }
        this.$swal({
          type: 'success',
          html: (exportData.payload && exportData.payload.html) ||
            `Exporting ${accountIds.length} transcripts — they'll appear in Drive shortly.`
        })
      } catch (err) {
        this.$swal({ type: 'error', text: `Couldn't export transcripts: ${err.message}` })
      } finally {
        this.loading = false
      }
    },

    // PRD §3.4.3 — per-user response summary, downloaded without an engineering request.
    // The rows come back as JSON and are turned into a CSV here with the same helper the
    // DbM report page uses, rather than streaming a file from the server: it keeps the
    // endpoint reusable and the filename/column naming in the UI layer where it belongs.
    async exportResponses () {
      this.loading = true
      try {
        const { data, status } = await api.buildRequest('nudge-campaign-analytics-export', 'POST', {
          campaignId: this.campaignId
        })
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        const payload = (data && data.payload) || {}
        const rows = payload.rows || []
        if (!rows.length) {
          this.$swal({ type: 'info', text: 'No users have been nudged by this campaign yet.' })
          return
        }

        const stageLabels = payload.stages || []
        const headers = [
          'User ID', 'First nudge at', 'Nudges sent', 'Delivered', 'Replied', 'Replied at',
          'Replied into', 'Session ID', 'Failure reason',
          ...stageLabels.map(label => `Reached: ${label}`)
        ]
        // Papa.unparse wants { fields, data } with data as positional arrays, and
        // downloadCSVCustomColumns writes `headers` as its own first line.
        const fields = [
          'user_id', 'first_nudge_at', 'nudges_sent', 'delivered', 'replied', 'replied_at',
          'replied_into', 'session_id', 'failure_reason',
          ...stageLabels.map((label, i) => `stage_${i}`)
        ]
        const body = rows.map(row => fields.map(field => {
          const value = row[field]
          if (value === null || value === undefined) return ''
          if (typeof value === 'boolean') return value ? 'Yes' : 'No'
          if (field.endsWith('_at')) return formatDate(value, 'd/M/Y hh:ss') || value
          return value
        }))

        const safeName = (payload.campaignName || 'campaign').replace(/[^\w.-]+/g, '_').slice(0, 80)
        downloadCSVCustomColumns({ fields, data: body }, headers, `nudge_responses_${safeName}`)

        if (payload.truncated) {
          this.$swal({
            type: 'warning',
            text: `Export capped at ${payload.maxRows.toLocaleString()} rows. Narrow the date range for the rest.`
          })
        }
      } catch (err) {
        this.$swal({ type: 'error', text: `Couldn't build the export: ${err.message}` })
      } finally {
        this.loading = false
      }
    },

    onRangeChange ({ from, to }) {
      this.trendFrom = from
      this.trendTo = to
      this.fetchDetail({ keepRange: true })
    },

    // First call has no range yet: the server returns `launched_at`, and the window is
    // derived from it and then re-requested. Re-deriving it client-side on every refresh
    // would silently discard whatever range the user had applied, hence `keepRange`.
    async fetchDetail ({ keepRange = false } = {}) {
      this.loading = true
      try {
        const { data, status } = await api.buildRequest('nudge-campaign-analytics-detail', 'POST', {
          campaignId: this.campaignId,
          fromDt: keepRange && this.trendFrom ? this.trendFrom : '',
          toDt: keepRange && this.trendTo ? `${this.trendTo} 23:59:59` : ''
        })
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        const payload = (data && data.payload) || {}
        this.detail = payload

        if (!keepRange) {
          const launched = payload.campaign && payload.campaign.launched_at
          const start = isoOf(launched)
          if (start) {
            const today = todayIso()
            const seventh = addDays(start, DEFAULT_TREND_DAYS - 1)
            this.trendFrom = start
            this.trendTo = seventh > today ? today : seventh
            // Refetch for the resolved window — the first response's `daily` covered the
            // campaign's whole life, which isn't what the chart is showing.
            await this.fetchDetail({ keepRange: true })
          }
        }
      } catch (err) {
        this.detail = null
        this.$swal({ type: 'error', text: `Couldn't load this campaign: ${err.message}` })
      } finally {
        this.loading = false
      }
    },

    // Nudges replied to ÷ nudges delivered. Both count MESSAGES, and both partition across
    // a campaign's A/B groups, so a variant's rate can no longer exceed its parent's. The
    // earlier version divided distinct responding USERS by delivered messages — mixed units,
    // and a distinct-user count doesn't partition, which is what made 23% and 14% variants
    // sit under an 8.6% campaign.
    responseRateOf (row) {
      return percentage(row.responded, row.delivered)
    },
    windowText (row) {
      return describeSession(row)
    },
    groupsText (row) {
      const groups = row.group_list || []
      return groups.length > 1 ? `A/B test · ${groups.length} variants` : 'Single version'
    },
    toggleRow (row) {
      // Vue 2 can't see a plain key assignment on an object, so replace it wholesale.
      this.expanded = { ...this.expanded, [row.id]: !this.expanded[row.id] }
    },
    reload () {
      if (this.campaignId) {
        this.fetchDetail()
        return
      }
      if (this.fromDt && this.toDt && Date.parse(this.toDt) < Date.parse(this.fromDt)) {
        this.$swal({ type: 'error', text: 'To must be on or after From' })
        return
      }
      this.expanded = {}
      this.fetchReport()
    },

    async fetchReport () {
      this.loading = true
      try {
        const { data, status } = await api.buildRequest('nudge-campaign-analytics', 'POST', {
          // Whole-day bounds. The endpoint compares against "user-engagements"."createdAt",
          // so the `to` date needs its end-of-day time or that day's sends are excluded.
          fromDt: this.fromDt || '',
          toDt: this.toDt ? `${this.toDt} 23:59:59` : '',
          searchText: this.searchText || '',
          nudgeConsoleOnly: this.scope === 'nudge-console'
        })
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        const payload = (data && data.payload) || {}
        this.campaigns = payload.campaigns || []
        this.truncated = Boolean(payload.truncated)
        this.limit = payload.limit || 0
      } catch (err) {
        this.campaigns = []
        this.truncated = false
        this.$swal({ type: 'error', text: `Couldn't load analytics: ${err.message}` })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped src="./nudge-console-theme.css"></style>
<style scoped src="./NudgeAnalytics.css"></style>



// WEBPACK FOOTER //
// NudgeAnalytics.vue?6b249700